/**
 * Holý rozmer vs. rozmer s hranou (KOLO 131).
 *
 * `x_mm`/`y_mm` dielca sú v celej appke rozmer **s hranou**, ako ho dáva
 * TopSolid (doska 500×400 s 1 mm hranami dookola = 502×402) — z neho
 * počíta nadmiera, prírez, cena aj Cut Rite. Keď je na zákazke zapnuté
 * „rozmery zadávam bez hrán", ručne zadaný rozmer je **holá doska**:
 * posiela sa ako `holy_x_mm` a backend k nemu hrany pripočíta. V tabuľke
 * ostane to, čo človek zadal; rozmer s hranou je v podtexte.
 */

export type PoleRozmeru = "x_mm" | "y_mm";

interface RozmeryDielca {
  x_mm?: string | number | null;
  y_mm?: string | number | null;
  holy_x_mm?: string | number | null;
  holy_y_mm?: string | number | null;
}

function holePole(pole: PoleRozmeru): "holy_x_mm" | "holy_y_mm" {
  return pole === "x_mm" ? "holy_x_mm" : "holy_y_mm";
}

/** Má dielec v tejto osi zadaný holý rozmer? */
export function maHolyRozmer(d: RozmeryDielca, pole: PoleRozmeru): boolean {
  return d[holePole(pole)] != null;
}

/** Čo ukázať v bunke: holý rozmer, ak je, inak rozmer s hranou. */
export function zobrazenyRozmer(
  d: RozmeryDielca,
  pole: PoleRozmeru,
): string | number | null | undefined {
  return maHolyRozmer(d, pole) ? d[holePole(pole)] : d[pole];
}

/**
 * O koľko je rozmer rodiča s hranou väčší než jeho holá doska (802 − 800).
 * `null`, keď rodič v tej osi holý rozmer nemá (dielec z TopSolidu).
 */
export function hranyRodica(
  rodic: RozmeryDielca | null | undefined,
  pole: PoleRozmeru,
): number | null {
  if (!rodic) return null;
  const holy = rodic[holePole(pole)];
  const plny = rodic[pole];
  if (holy == null || plny == null) return null;
  return Number(plny) - Number(holy);
}

const naStotiny = (n: number): number => Math.round(n * 100) / 100;

/**
 * Rozmery vrstvy sendviča tak, ako ich ukazuje rodič (KOLO 138).
 *
 * Vrstva hrany nemá — olepuje sa celý sendvič hranami rodiča — a nesie
 * rozmer rodiča **s hranou** (802×602), lebo z neho ráta prírez aj porez.
 * User ju chce vidieť ako rodiča: *„vrstvy pod tým musia mať tiež rozmer
 * 800×600"*. Holá doska vrstvy = jej rozmer − hrany rodiča v tej osi.
 */
export function rozmeryVrstvy<T extends RozmeryDielca>(
  vrstva: T,
  rodic: RozmeryDielca | null | undefined,
): T & Pick<RozmeryDielca, "holy_x_mm" | "holy_y_mm"> {
  const out: T & Pick<RozmeryDielca, "holy_x_mm" | "holy_y_mm"> = { ...vrstva };
  for (const pole of ["x_mm", "y_mm"] as const) {
    const h = hranyRodica(rodic, pole);
    const plny = vrstva[pole];
    if (h != null && plny != null) {
      out[holePole(pole)] = naStotiny(Number(plny) - h);
    }
  }
  return out;
}

/**
 * Telo PATCH/POST pre ručne zadaný rozmer.
 *
 * Holý sa posiela len pri zapnutom prepínači a nie na vrstve sendviča —
 * vrstva hrany nemá. Vrstva, ktorá sa ukazuje ako rodič (holá doska,
 * KOLO 138), uloží zadanú hodnotu + hrany rodiča, teda rozmer s hranou.
 */
export function rozmerNaUlozenie(
  pole: PoleRozmeru,
  hodnota: string | null,
  {
    bezHran,
    jeVrstva = false,
    hranyRodica: hrany = null,
  }: { bezHran: boolean; jeVrstva?: boolean; hranyRodica?: number | null },
): Record<string, string | null> {
  if (jeVrstva) {
    if (hrany != null && hodnota != null && hodnota !== "") {
      return { [pole]: String(naStotiny(Number(hodnota) + hrany)) };
    }
    return { [pole]: hodnota };
  }
  if (bezHran) return { [holePole(pole)]: hodnota };
  return { [pole]: hodnota };
}
